import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import type { CatalogItemDetail, CatalogItemSummary, StorePrice } from "@/lib/api/types";
import { getCsrfHeaders } from "@/lib/api/csrf";
import { trackBeginCheckout } from "@/lib/analytics-events";
import { hasSellablePrice, isPurchasable } from "@/lib/utils/price";

export interface StoreCartItem {
  productId: number;
  slug: string;
  name: string;
  image?: string | null;
  price?: StorePrice | null;
  quantity: number;
}

export interface StoreCheckoutResult {
  order_id: number;
  amount_total: number;
  currency: string;
  payment_url?: string;
}

interface StoreCartContextValue {
  items: StoreCartItem[];
  count: number;
  isOpen: boolean;
  bumping: boolean;
  add: (product: CatalogItemSummary | CatalogItemDetail, qty?: number) => void;
  remove: (productId: number) => void;
  updateQty: (productId: number, qty: number) => void;
  clear: () => void;
  openDrawer: () => void;
  closeDrawer: () => void;
  submitOrder: (addresses?: {
    shipping_partner_id?: number;
    billing_partner_id?: number;
  }) => Promise<StoreCheckoutResult>;
}

const StoreCartContext = createContext<StoreCartContextValue | null>(null);
const STORAGE_KEY = "vars.store-cart.v1";

function readStoredItems(): StoreCartItem[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const stored: StoreCartItem[] = raw ? JSON.parse(raw) : [];
    // Carts saved before VARS-121 can hold zero-priced lines, which check out
    // as a zero-value order. Drop them on read rather than carrying them.
    return stored.filter((item) => hasSellablePrice(item.price));
  } catch {
    return [];
  }
}

export function StoreCartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<StoreCartItem[]>(readStoredItems);
  const [isOpen, setIsOpen] = useState(false);
  const [bumping, setBumping] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    }
  }, [items]);

  const bump = useCallback(() => {
    setBumping(true);
    setTimeout(() => setBumping(false), 450);
  }, []);

  const add = useCallback(
    (product: CatalogItemSummary | CatalogItemDetail, qty = 1) => {
      // The chokepoint for the availability and price rules: no caller can put
      // an unavailable item, or one Odoo prices at 0.00, into the cart —
      // whatever its own UI does. isPurchasable() is the same predicate the
      // cards and the reorder action use to decide what to offer.
      const purchase = product.purchase;
      // `purchase` is re-tested only to narrow the type: isPurchasable() has
      // already established it is non-null, but a predicate over a property
      // cannot tell TypeScript that.
      if (!purchase || !isPurchasable(product)) return;
      const productId = purchase.product_id;
      const price: StorePrice = {
        amount: purchase.amount,
        currency: purchase.currency,
      };
      setItems((prev) => {
        const existing = prev.find((i) => i.productId === productId);
        if (existing) {
          return prev.map((i) =>
            i.productId === productId ? { ...i, quantity: i.quantity + qty } : i,
          );
        }
        return [
          ...prev,
          {
            productId,
            slug: product.slug,
            name: product.name,
            image: product.primary_media?.url ?? null,
            price,
            quantity: qty,
          },
        ];
      });
      bump();
    },
    [bump],
  );

  const remove = (productId: number) =>
    setItems((prev) => prev.filter((i) => i.productId !== productId));

  const updateQty = (productId: number, qty: number) =>
    setItems((prev) =>
      prev.map((i) => (i.productId === productId ? { ...i, quantity: Math.max(1, qty) } : i)),
    );

  const clear = () => setItems([]);

  const count = items.reduce((n, i) => n + i.quantity, 0);

  return (
    <StoreCartContext.Provider
      value={{
        items,
        count,
        isOpen,
        bumping,
        add,
        remove,
        updateQty,
        clear,
        openDrawer: () => setIsOpen(true),
        closeDrawer: () => setIsOpen(false),
        submitOrder: async (addresses) => {
          const response = await fetch("/api/store/checkout", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...getCsrfHeaders(),
            },
            body: JSON.stringify({
              items: items.map((i) => ({ product_id: i.productId, qty: i.quantity })),
              shipping_partner_id: addresses?.shipping_partner_id,
              billing_partner_id: addresses?.billing_partner_id,
            }),
          });
          if (!response.ok) {
            const errBody = (await response.json().catch(() => ({}))) as {
              error?: string;
              message?: string;
            };
            throw new Error(errBody.message || "Failed to submit order");
          }
          const result = (await response.json()) as StoreCheckoutResult;
          // The last thing this site can observe. Payment happens inside Odoo's
          // portal (ADR-013), so completion is measure M5 and is Odoo-side only.
          // Fired before clear(), which empties `items`.
          trackBeginCheckout({ itemCount: items.length });
          clear();
          return result;
        },
      }}
    >
      {children}
    </StoreCartContext.Provider>
  );
}

export function useStoreCart() {
  const ctx = useContext(StoreCartContext);
  if (!ctx) throw new Error("useStoreCart must be used inside StoreCartProvider");
  return ctx;
}
